import React, { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router';

import { Divider } from '@mui/material';

import useIsMounted from 'hooks/use-is-mounted';
import LicenseGuard from 'main/components/LicenseGuard';
import AuthWidget from 'pages/components/AuthWidget';
import { MenuContextProvider } from 'pages/layout/components/MenuContext';
import MenuItem from 'pages/layout/components/MenuItem';
import { getRouteInfoByLink, menuRoutes } from 'pages/layout/components/MenuList/routes';

import useStyles from './styles';

interface Props {
    className?: string;
    additional?: React.ReactNode;
    onMenuItemClick?: () => void;
}

const MenuList: React.FC<Props> = ({ className, additional, onMenuItemClick }) => {
    const { classes, cx } = useStyles();
    const location = useLocation();
    const isMounted = useIsMounted();

    const contextValue = useMemo(() => {
        const { active, expanded } = getRouteInfoByLink(location.pathname);
        return { active, expanded };
    }, [location.pathname]);

    const renderRoutes = useCallback(
        (routes: typeof menuRoutes, indent = 0) =>
            routes
                .filter(({ virtual }) => !virtual)
                .map(({ key, link, text, divider, icon, children = [], ...rest }) => (
                    <LicenseGuard
                        key={key}
                        enabled={key === 'assessment_perimeter_control'}
                        licenses={['trial', 'paid']}
                    >
                        {divider === 'top' || divider === 'both' ? <Divider className={classes.divider} /> : null}
                        <MenuItem
                            itemKey={key}
                            indent={indent}
                            link={link}
                            text={text}
                            icon={icon}
                            onClick={onMenuItemClick}
                            {...rest}
                        >
                            {renderRoutes(children, indent + 1)}
                        </MenuItem>
                        {divider === 'bottom' || divider === 'both' ? <Divider className={classes.divider} /> : null}
                    </LicenseGuard>
                )),
        [classes.divider, onMenuItemClick]
    );

    return (
        <MenuContextProvider value={contextValue}>
            <div className={cx(classes.container, className)}>
                <div className={classes.wrapper}>
                    <div className={classes.menuList}>{renderRoutes(menuRoutes)}</div>
                </div>
                {additional}
                <AuthWidget hidden={!isMounted} />
            </div>
        </MenuContextProvider>
    );
};

export default MenuList;
